import React, {Component, useEffect, useState} from "react";
import {useNavigate, useLocation} from "react-router";
import FormLayout from "../FormLayout";
import ChangePassword from "./ChangePassword";
import { validateFormExt, validateUsername } from "../validation";
import {
  getMessageObject,
  MessageType,
} from "../FormLayout/MessageFactory";
import { inject, observer } from "mobx-react";
import { toast } from "react-toastify";

const _ERRORS = {
  PasswordReused: "That password has been recently used",
  PasswordValidationError: "The new password could not be validated, please try again later",
  NoResponse: "The server is currently unreachable"
};

const lookupError = name => {
  return _ERRORS[name] || name;
};

const ACTION_REQUEST = "request-link";
const ACTION_CHANGE = "change-password";

// This container implements both change password and password reset functionality
// In the former case, the old password is required; in the latter, a verification code
// ACTION_REQUEST: send verification code via email
// ACTION_CHANGE: change password, with either old password or verification code
const PasswordModificationContainer = (props) => {

  const {userStore} = props;

  const location = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(location.search);
  const input = {...props, ...location.state};

  const {
    action = params.get('confirmation_code') ? ACTION_CHANGE : params.get('action') || ACTION_CHANGE,
  } = input;

  const [username, setUsername] = useState(input.username || params.get("email") || "");
  const initialCode = input.verificationCode || params.get('confirmation_code');
  // Use "" (not null/undefined) when in reset flow so validation and submit treat it as code mode
  const [verificationCode, setVerificationCode] = useState(
    initialCode ?? (input.accessLinkSent ? "" : null)
  );
  const [accessLinkSent, setAccessLinkSent] = useState(input.accessLinkSent || verificationCode != null);
  // True when user clicked "I already have a reset code" — code fields stay visible even while editing email
  const [skippedToCode, setSkippedToCode] = useState(input.accessLinkSent && !initialCode);
  const [oldPassword, setOldPassword] = useState(input.password || "");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordCheck, setNewPasswordCheck] = useState("");
  const [validating, setValidating] = useState(false);
  const [messageObject, setMessageObject] = useState(input.message || null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);
  const [fieldErrors, setFieldErrors] = useState(
    {
      username: username !== "",
      verificationCode: false,
      oldPassword: false,
      newPassword: false,
      newPasswordCheck: false
    }
  );
  const [newPasswordInvalid, setNewPasswordInvalid] = useState(null);

  const requestAccessLink = requestedBefore => evt => {
    // Use different state depending on whether this is initial request or resend
    if (requestedBefore) {
      setResendingCode(true);
    } else {
      setFormSubmitted(true);
    }

    userStore
      .sendVerificationCode(username)
      .then(result => {
        setFormSubmitted(false);
        setResendingCode(false);
        setAccessLinkSent(true);
        // Use toast for informational messages
        const msg = requestedBefore
          ? `A new password reset code has been sent to ${username || "your email"}. Copy the code from your email and enter it below along with your new password.`
          : `A password reset code has been sent to ${username || "your email"}. Copy the code from your email and enter it below along with your new password.`;
        toast.info(msg);
        setFieldErrors({
                          username: false,
                          verificationCode: false,
                          newPassword: false,
                          newPasswordCheck: false
                        });
      })
      .catch(error => {
        setAccessLinkSent(false);
        setMessageObject(null);
        // "InvalidParameterException" is raised when user is in "unconfirmed" state
        if (error.name === "InvalidParameterException") {
          navigate("/signin", {
            replace: true,
            state: {message: getMessageObject(MessageType.UserNotConfirmedException, username)}
          });
          return;
        } else if (error.name === "UserNotFoundException") {
          setMessageObject(getMessageObject(
            MessageType.UserNotFoundException,
            username
          ));
        } else if (error.name === "LimitExceededException") {
          setAccessLinkSent(false);
          setMessageObject(getMessageObject(error));
        }
        setFormSubmitted(false);
        setResendingCode(false);
        setFieldErrors({
                          username: false,
                          verificationCode: false,
                          oldPassword: false,
                          newPassword: false,
                          newPasswordCheck: false
                        });
      });
  };

  const changePassword = () => {
    userStore
      .changePassword(username, oldPassword, newPassword)
      .then(result => {
        navigate("/", {
          replace: true,
          state: {
            username: username,
            password: newPassword,
            message: {message: "Password was changed", type: "info", name: "info"}
          }
        });
      })
      .catch(error => {
        // e.g. 400: name="LimitExceededException"
        // NotAuthorizedException = wrong current password — expected user error, not a bug
        if (error.name !== "NotAuthorizedException" && window.Sentry) {
          window.Sentry.withScope(scope => {
            scope.setContext('password_change', {
              username: username,
              error_name: error.name,
              error_message: error.message,
              error_status: error?.status
            });
            scope.setLevel('error');
            scope.setTag('auth_operation', 'change_password');
            window.Sentry.captureException(error);
          });
        }
        setFormSubmitted(false);
        setMessageObject({message: error.message, type: "error", name: error.name});
      });
  };

  const confirmNewPassword = async () => {
    try {
      await userStore.confirmNewPassword(
        username,
        verificationCode,
        newPassword
      );

      try {
        await userStore.login(username, newPassword, navigate);
        navigate("/", {replace: true});
      } catch (signInError) {
        // After successful password reset, NotAuthorizedException means the
        // PreAuthentication Lambda rejected the user — expected, not a bug.
        if (signInError.name === "NotAuthorizedException") {
          console.warn("Auto-login after password reset rejected by pre-auth Lambda", { name: signInError.name, message: signInError.message });
        } else {
          console.error("Auto-login after password reset failed", signInError);
          if (window.Sentry) {
            window.Sentry.withScope(scope => {
              scope.setContext('password_reset', {
                username: username,
                error_name: signInError.name,
                error_message: signInError.message,
                step: 'auto_login_after_reset'
              });
              window.Sentry.captureException(signInError);
            });
          }
        }

        const message = signInError.name === "NotAuthorizedException"
          ? getMessageObject(MessageType.NotAuthorized)
          : {message: `Password was successfully reset, but automatic login failed. Please sign in with your new password. (${signInError.message})`,
             type: "warning", name: signInError.name};

        navigate("/signin", {
          replace: true,
          state: {
            username: username,
            password: signInError.name === "NotAuthorizedException" ? "" : newPassword,
            message
          }});
      }
    } catch (error) {
      // LimitExceededException, ExpiredCodeException, CodeMismatchException — real errors worth tracking.
      // NotAuthorizedException = expected auth rejection, not a bug.
      if (error.name !== "NotAuthorizedException" && window.Sentry) {
        window.Sentry.withScope(scope => {
          scope.setContext('password_reset', {
            username: username,
            error_name: error.name,
            error_message: error.message,
            error_status: error?.status,
            step: 'confirm_new_password'
          });
          scope.setLevel('error');
          scope.setTag('auth_operation', 'confirm_reset');
          window.Sentry.captureException(error);
        });
      }
      setFormSubmitted(false);
      setMessageObject({message: error.message, type: "error", name: error.name});
    }
  };

  const handleChange = field => evt => {
    const value = evt.target.value;
    setMessageObject(null);
    if (field === "username") {
      setUsername(value.trim());
      if (!skippedToCode) setAccessLinkSent(false);
    }
    else if (field === "verificationCode") {
      setVerificationCode(value);
    }
    else if (field === "oldPassword") {
      setOldPassword(value);
    }
    else if (field === "newPasswordCheck") {
      setNewPasswordCheck(value);
    }
    else if (field === "newPassword") {
      setNewPassword(value);

      // PLEX-1623: Only call password validation if username is valid
      const usernameError = validateUsername(username);
      if (usernameError) {
        // Don't call API if username is invalid - just clear any previous validation error
        setNewPasswordInvalid(false);
        return;
      }

      setValidating(true);
      userStore
        .runServerPasswordValidation(
          username,
          value,
          action === ACTION_REQUEST
        )
        .then(() => {
          setNewPasswordInvalid(false);
        })
        .catch(error => {
          const errorContent = error?.content || error?.message;
          const isPasswordReused = errorContent === "PasswordReused";

          if (!isPasswordReused && window.Sentry) {
            window.Sentry.withScope(scope => {
              scope.setContext('password_validation', {
                username: username,
                action: action,
                error_content: errorContent,
                error_name: error?.name,
                error_status: error?.status,
                error_message: error?.message,
                full_error: JSON.stringify(error, Object.getOwnPropertyNames(error))
              });
              scope.setLevel('warning');
              scope.setTag('validation_type', 'password');
              scope.setTag('auth_flow', action === ACTION_REQUEST ? 'reset' : 'change');
              window.Sentry.captureException(
                new Error(`Password validation failed: ${errorContent || 'Unknown error'}`)
              );
            });
          }

          if (isPasswordReused) {
            setNewPasswordInvalid(lookupError("PasswordReused"));
          } else {
            // Server error — don't block submit, just warn
            setNewPasswordInvalid(false);
            setMessageObject({message: lookupError("PasswordValidationError"), type: "warning"});
          }
        })
        .finally(() => {
          // PLEX-1622: Always stop the spinner, even if there was an error
          setValidating(false);
        })
    }
  };

  // Install this as an onFocus handler if you want any error messages to be cleared
  // when the user is using the field
  const handleFocus = (field, focused) => evt => {
    setFieldErrors({...fieldErrors, [field]: !focused });
  };

  const errors = validateFormExt(
    username,
    verificationCode,
    oldPassword,
    newPassword,
    newPasswordCheck,
    newPasswordInvalid,
    validating,
  );
  useEffect(() => {
    let errorMsg = "";
    if (fieldErrors.username && errors.username) {
      errorMsg = errors.username;
    }
    if (fieldErrors.verificationCode && errors.verificationCode) {
      errorMsg += (errorMsg !== "" ? "\n" : "") + errors.verificationCode;
    }
    if (fieldErrors.oldPassword && errors.oldPassword) {
      errorMsg += (errorMsg !== "" ? "\n" : "") + errors.oldPassword;
    }
    if (fieldErrors.newPassword && errors.newPassword) {
      errorMsg += (errorMsg !== "" ? "\n" : "") + errors.newPassword;
    }
    if (newPasswordInvalid) {
      errorMsg += (errorMsg !== "" ? "\n" : "") + newPasswordInvalid;
    }
    if (fieldErrors.newPasswordCheck && errors.newPasswordCheck) {
      errorMsg += (errorMsg !== "" ? "\n" : "") + errors.newPasswordCheck;
    }
    if (errorMsg !== "") {
      setMessageObject({message: errorMsg, type: "error"});
    }
  }, [errors, fieldErrors]);

  return (
    <FormLayout message={messageObject}>
      <ChangePassword
        action={action}
        formSubmitted={formSubmitted}
        resendingCode={resendingCode}
        username={username}
        accessLinkHasBeenSent={accessLinkSent}
        skippedToCode={skippedToCode}
        verificationCode={verificationCode}
        oldPassword={oldPassword}
        newPassword={newPassword}
        validating={validating}
        newPasswordCheck={newPasswordCheck}
        errors={errors}
        onRequestAccessLink={requestAccessLink}
        onSkipToCode={() => {
          setAccessLinkSent(true);
          setSkippedToCode(true);
          setVerificationCode("");
        }}
        onChange={handleChange}
        onFocus={handleFocus}
        onSubmit={() => {
          setFormSubmitted(true);
          setValidating(false); // Clear validation spinner to avoid duplicate spinners
          setMessageObject(null); // Clear any displayed error messages on submit
          setFieldErrors({ // Clear all field-level error flags on submit
            username: false,
            verificationCode: false,
            oldPassword: false,
            newPassword: false,
            newPasswordCheck: false
          });
          if (action === ACTION_REQUEST) {
            confirmNewPassword();
          }
          else {
            changePassword();
          }
        }}
        onCancel={() => {
          if (action === ACTION_CHANGE) {
            navigate(-1);
          } else {
            navigate("/signin", {replace: true, state: {username}});
          }
        }}
      />
    </FormLayout>
  );
};

const RequestAccessLink = inject("userStore")(
  observer(props => (
    <PasswordModificationContainer {...props} action={ACTION_REQUEST} />
  ))
);

const PasswordChange = inject("userStore")(
  observer(PasswordModificationContainer)
);

export { ACTION_REQUEST, ACTION_CHANGE, PasswordChange, RequestAccessLink };
